/**
 * Lab Performance Hooks
 * Hooks for main dashboard, two-tier analytics, users, and daily performance
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { type ApiErrorResponse, getUserFriendlyError, safeParseJson } from '@utils/api';
import { useCallback, useEffect, useState } from 'react';
import type {
  DailyPerformance,
  LabDashboardData,
  LabFilters,
  TwoTierAnalytics,
  UserWithTrackedPicks,
} from './labDashboardTypes';
import { DEFAULT_FILTERS } from './labDashboardTypes';

// =============================================================================
// Result Types
// =============================================================================

export interface UseLabDashboardResult {
  data: LabDashboardData | null;
  loading: boolean;
  error: string | null;
  filters: LabFilters;
  setFilters: (filters: Partial<LabFilters>) => void;
  refresh: () => Promise<void>;
}

export interface UseTwoTierAnalyticsResult {
  data: TwoTierAnalytics | null;
  loading: boolean;
  error: string | null;
  recentDays: number;
  setRecentDays: (days: number) => void;
  refresh: () => Promise<void>;
}

export interface UseUsersWithTrackedPicksResult {
  users: UserWithTrackedPicks[];
  loading: boolean;
  error: string | null;
}

export interface UseDailyPerformanceResult {
  data: DailyPerformance[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// =============================================================================
// useLabDashboard
// =============================================================================

export function useLabDashboard(): UseLabDashboardResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [data, setData] = useState<LabDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<LabFilters>(DEFAULT_FILTERS);

  const setFilters = useCallback((newFilters: Partial<LabFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const fetchDashboard = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query params from filters
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.config) {
        params.set('config', filters.config);
      }
      if (filters.tier) {
        params.set('tier', filters.tier);
      }
      if (filters.propType) {
        params.set('propType', filters.propType);
      }
      if (filters.direction) {
        params.set('direction', filters.direction);
      }
      if (filters.minSampleSize !== 5) {
        params.set('minSampleSize', filters.minSampleSize.toString());
      }
      if (filters.scope && filters.scope !== 'all') {
        params.set('scope', filters.scope);
      }
      if (filters.scope === 'user' && filters.userId) {
        params.set('userId', filters.userId.toString());
      }

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/lab/dashboard${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      const result = await safeParseJson<LabDashboardData | ApiErrorResponse>(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setData(result as LabDashboardData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch dashboard';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters]);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return {
    data,
    loading,
    error,
    filters,
    setFilters,
    refresh: fetchDashboard,
  };
}

// =============================================================================
// useTwoTierAnalytics
// =============================================================================

export function useTwoTierAnalytics(filters: LabFilters): UseTwoTierAnalyticsResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [data, setData] = useState<TwoTierAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentDays, setRecentDays] = useState(7);

  const fetchTwoTierAnalytics = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query params from filters
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.config) {
        params.set('config', filters.config);
      }
      if (filters.tier) {
        params.set('tier', filters.tier);
      }
      if (filters.propType) {
        params.set('propType', filters.propType);
      }
      if (filters.scope && filters.scope !== 'all') {
        params.set('scope', filters.scope);
      }
      if (filters.scope === 'user' && filters.userId) {
        params.set('userId', filters.userId.toString());
      }
      params.set('recentDays', recentDays.toString());

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/lab/two-tier${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      const result = await safeParseJson<TwoTierAnalytics | ApiErrorResponse>(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setData(result as TwoTierAnalytics);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch two-tier analytics';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters, recentDays]);

  // Fetch on mount and when filters/recentDays change
  useEffect(() => {
    fetchTwoTierAnalytics();
  }, [fetchTwoTierAnalytics]);

  return {
    data,
    loading,
    error,
    recentDays,
    setRecentDays,
    refresh: fetchTwoTierAnalytics,
  };
}

// =============================================================================
// useUsersWithTrackedPicks
// =============================================================================

export function useUsersWithTrackedPicks(): UseUsersWithTrackedPicksResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [users, setUsers] = useState<UserWithTrackedPicks[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchUsers() {
      if (!isAuthenticated) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const url = `${API_BASE_URL}/api/lab/users`;
        const response = await authFetch(url);

        const result = await safeParseJson<{ users: UserWithTrackedPicks[] } | ApiErrorResponse>(
          response,
        );

        if (!response.ok) {
          const errorResult = result as ApiErrorResponse;
          throw new Error(getUserFriendlyError(errorResult.error));
        }

        setUsers((result as { users: UserWithTrackedPicks[] }).users);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, [isAuthenticated, authFetch]);

  return { users, loading, error };
}

// =============================================================================
// useDailyPerformance
// =============================================================================

export function useDailyPerformance(
  filters: LabFilters,
  limit: number = 30,
): UseDailyPerformanceResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [data, setData] = useState<DailyPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDailyPerformance = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.dateRange && filters.dateRange !== 'all') {
        params.set('dateRange', filters.dateRange);
      }
      if (filters.config) {
        params.set('config', filters.config);
      }
      if (filters.tier) {
        params.set('tier', filters.tier);
      }
      if (filters.propType) {
        params.set('propType', filters.propType);
      }
      if (filters.scope && filters.scope !== 'all') {
        params.set('scope', filters.scope);
      }
      if (filters.scope === 'user' && filters.userId) {
        params.set('userId', filters.userId);
      }
      params.set('limit', limit.toString());

      const queryString = params.toString();
      const url = `${API_BASE_URL}/api/lab/daily-performance${queryString ? `?${queryString}` : ''}`;

      const response = await authFetch(url);

      const result = await safeParseJson<
        { dailyPerformance: DailyPerformance[] } | ApiErrorResponse
      >(response);

      if (!response.ok) {
        const errorResult = result as ApiErrorResponse;
        throw new Error(getUserFriendlyError(errorResult.error));
      }

      setData((result as { dailyPerformance: DailyPerformance[] }).dailyPerformance);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch daily performance';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch, filters, limit]);

  useEffect(() => {
    fetchDailyPerformance();
  }, [fetchDailyPerformance]);

  return { data, loading, error, refresh: fetchDailyPerformance };
}
